import React from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";

const REVIEWS = [
  {
    initials: "MG",
    name: "María G.",
    city: "Houston, TX",
    photo: "https://randomuser.me/api/portraits/women/68.jpg",
    quote: {
      es: "Me activaron en menos de 30 minutos. Todo en español, sin sorpresas. Mi factura bajó casi $50 al mes.",
      en: "They activated my service in under 30 minutes. Everything in Spanish, no surprises. My bill dropped almost $50/mo.",
    },
  },
  {
    initials: "CR",
    name: "Carlos R.",
    city: "Dallas, TX",
    photo: "https://randomuser.me/api/portraits/men/32.jpg",
    quote: {
      es: "El asesor verificó mi ESIID al instante. Me explicó cada cargo. Soy cliente desde hace 8 meses, sin un solo problema.",
      en: "The advisor verified my ESIID instantly. Explained every charge. I've been a customer 8 months — zero issues.",
    },
  },
  {
    initials: "LM",
    name: "Luis M.",
    city: "Fort Worth, TX",
    photo: "https://randomuser.me/api/portraits/men/55.jpg",
    quote: {
      es: "Mi local comercial necesitaba claridad. Velcor me orientó por WhatsApp y activó el servicio en 2 días.",
      en: "My business needed clarity. Velcor guided me on WhatsApp and activated service in 2 days.",
    },
  },
  {
    initials: "JS",
    name: "Jessica S.",
    city: "Austin, TX",
    photo: "https://randomuser.me/api/portraits/women/45.jpg",
    quote: {
      es: "Acababa de mudarme y no entendía nada del PUCT. El equipo me explicó todo paso a paso. Recomendado al 100%.",
      en: "I had just moved and didn't understand anything about PUCT. The team walked me through it all. 100% recommended.",
    },
  },
  {
    initials: "RP",
    name: "Roberto P.",
    city: "San Antonio, TX",
    photo: "https://randomuser.me/api/portraits/men/41.jpg",
    quote: {
      es: "Sin contratos engañosos, sin letras chiquitas. Por fin un proveedor honesto. Cambié de Reliant y no me arrepiento.",
      en: "No tricky contracts, no fine print. Finally an honest provider. I switched from Reliant and have no regrets.",
    },
  },
  {
    initials: "AT",
    name: "Ana T.",
    city: "El Paso, TX",
    photo: "https://randomuser.me/api/portraits/women/22.jpg",
    quote: {
      es: "Atención al cliente impecable. Me respondieron por WhatsApp en menos de 5 minutos un domingo. Increíble.",
      en: "Impeccable customer service. They answered me on WhatsApp in under 5 minutes on a Sunday. Incredible.",
    },
  },
  {
    initials: "DH",
    name: "Daniel H.",
    city: "McAllen, TX",
    photo: "https://randomuser.me/api/portraits/men/77.jpg",
    quote: {
      es: "Activación rápida, factura clara, ahorro real. No tengo nada malo que decir. Velcor cumplió todo lo prometido.",
      en: "Fast activation, clear bill, real savings. Nothing bad to say. Velcor delivered on every promise.",
    },
  },
  {
    initials: "PV",
    name: "Patricia V.",
    city: "Plano, TX",
    photo: "https://randomuser.me/api/portraits/women/12.jpg",
    quote: {
      es: "Llevaba años pagando de más con TXU. Velcor me hizo el cálculo real con mi consumo y ahora ahorro $40/mes.",
      en: "I'd been overpaying with TXU for years. Velcor calculated based on my real usage — now I save $40/mo.",
    },
  },
];

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-3.5 h-3.5" aria-hidden>
    <path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.7 33.4 30 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l6.4-6.4C34.4 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.7 20-21 0-1.4-.2-2.7-.5-4z" />
    <path fill="#34A853" d="M6.3 14.7l7 5.1C15.3 16.2 19.3 13 24 13c3.1 0 5.9 1.2 8 3.1l6.4-6.4C34.4 5.1 29.5 3 24 3 16.3 3 9.7 7.6 6.3 14.7z" />
    <path fill="#FBBC05" d="M24 45c5.4 0 10.3-2 14-5.3l-6.5-5.5c-2.1 1.4-4.7 2.3-7.5 2.3-5.9 0-10.9-3.9-12.7-9.3l-7.1 5.5C7.6 39.7 15.2 45 24 45z" />
    <path fill="#EA4335" d="M44.5 20H24v8.5h11.7c-1 2.4-2.7 4.5-4.7 6l6.5 5.5C42.7 36.3 45 30.7 45 24c0-1.4-.2-2.7-.5-4z" />
  </svg>
);

const ReviewCard = ({ r, lang, t }) => (
  <div
    className="glass-strong rounded-[24px] p-5 md:p-6 relative shrink-0 w-[330px] md:w-[380px]"
    data-testid={`google-review-${r.initials}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-1 text-amber-400">
        {Array.from({ length: 5 }).map((_, k) => (
          <Star key={k} className="w-3.5 h-3.5 fill-current" />
        ))}
      </div>
      <GoogleG />
    </div>
    <p className="text-slate-700 text-[14.5px] leading-relaxed mb-5 font-medium line-clamp-4">
      "{r.quote[lang]}"
    </p>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-3 min-w-0">
        <img
          src={r.photo}
          alt={r.name}
          className="rounded-full object-cover ring-2 ring-velcor-green/15 shrink-0"
          width="40"
          height="40"
          loading="lazy"
        />
        <div className="min-w-0">
          <div className="font-black text-velcor-ink text-[14px] leading-tight truncate">{r.name}</div>
          <div className="text-slate-500 text-[12px] font-bold truncate">{r.city}</div>
        </div>
      </div>
      <span
        className="pill px-2 py-1 text-[10px] font-extrabold border border-slate-200 text-slate-500 bg-white inline-flex items-center gap-1 shrink-0"
        title={t("home.googleVerified")}
      >
        <GoogleG />
        {lang === "es" ? "Verificada" : "Verified"}
      </span>
    </div>
  </div>
);

export default function GoogleReviewCards() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  // Duplicate the list so the marquee loop is seamless
  const loop = [...REVIEWS, ...REVIEWS];

  return (
    <section className="max-w-[1340px] mx-auto px-5 py-16 md:py-20" data-testid="google-reviews">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3">
          <GoogleG />
          <span className="font-black text-[11.5px] tracking-[0.18em] text-slate-500 uppercase">
            Google Reviews
          </span>
        </div>
        <div className="flex items-center justify-center gap-1 mb-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
          ))}
          <span className="font-black text-velcor-ink text-[18px] ml-2">4.9</span>
          <span className="text-slate-500 text-[13px] font-bold ml-1.5">· 1,400+</span>
        </div>
        <p className="text-slate-500 text-[14px] font-medium">
          {lang === "es"
            ? "Reseñas verificadas de tejanos que ya activaron con Velcor."
            : "Verified reviews from Texans who activated with Velcor."}
        </p>
      </div>

      {/* Marquee */}
      <div className="reviews-marquee relative overflow-hidden">
        {/* Edge fade masks */}
        <div className="reviews-marquee__fade reviews-marquee__fade--left" aria-hidden />
        <div className="reviews-marquee__fade reviews-marquee__fade--right" aria-hidden />

        <div className="reviews-marquee__track">
          {loop.map((r, i) => (
            <ReviewCard key={`${r.initials}-${i}`} r={r} lang={lang} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
